<template>
  <div
    :class="[
      $style.distributionGroupManagement,
      $style.rowSpacing,
    ]"
  >
    <CheckboxWithLabel
      :data-test-id="$testId('groupManagementCheckbox')"
      :label="$options.propsData.checkboxLabel"
      :preventDefault="true"
      :tooltip="getTooltip"
      :value="groupsEnabled"
      @change="handleChange($event)"
    />
    <AnnouncementWrapper
      v-if="announcement.enabled"
      caretPosition="left"
      :class="directorySpecificProps.className"
      :customCaretClassName="$style.announcementCaretPosition"
      :header="announcement.header"
      :intro="announcement.intro"
      :localStorageKey="announcement.localStorageKey"
      :mainActionLink="tooltipKbLink"
      mainActionText="Learn More"
      :text="announcement.text"
    />
  </div>
</template>
<script>

import {
  DirectoryNameConsts, DirectoryTypeConsts, DirectoryTypeKeys, StoreModelMap,
} from 'client/util/Constants';
import { mapActions, mapState } from 'vuex';
import AnnouncementWrapper from 'components/Announcement/AnnouncementWrapper.vue';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import CheckboxWithLabel from 'client/components/FormValidation/CheckboxWithLabel.vue';
import GSuiteApi from 'client/api/v2/GSuiteApi';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import O365Api from 'client/api/v2/O365Api';
import pluralize from 'pluralize';

const activeDirectoryDisplayName = DirectoryNameConsts[DirectoryTypeConsts.activeDirectory];
const googleDisplayName = DirectoryNameConsts[DirectoryTypeConsts.gSuite];
const o365DisplayName = DirectoryNameConsts[DirectoryTypeConsts.office365];

export default {
  name: 'DistributionGroupManagement',

  components: {
    AnnouncementWrapper,
    CheckboxWithLabel,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  props: {
    announcement: {
      type: Object,
      default() {
        return {
          enabled: false,
          header: '',
          intro: '',
          localStorageKey: '',
          text: '',
        };
      },
    },
    checkboxLabel: {
      type: String,
      default: '',
    },
    tooltipKbLink: {
      type: String,
      default: '',
    },
    tooltipLinkText: {
      type: String,
      default: '',
    },
    tooltipText: {
      type: String,
      default: '',
    },
  },

  computed: {
    directorySpecificProps() {
      const result = {};
      switch (this.parentType) {
        case DirectoryTypeConsts[DirectoryTypeKeys.activeDirectory]:
          result.api = GSuiteApi;
          result.className = this.$style.announcementAd;
          result.displayName = activeDirectoryDisplayName;
          result.supportedGroupType = 'Distribution';
          break;
        case DirectoryTypeConsts[DirectoryTypeKeys.gSuite]:
          result.api = GSuiteApi;
          result.className = this.$style.announcementGSuite;
          result.displayName = googleDisplayName;
          result.supportedGroupType = 'Distribution';
          break;
        case DirectoryTypeConsts[DirectoryTypeKeys.office365]:
          result.api = O365Api;
          result.className = this.$style.announcementGSuite;
          result.displayName = o365DisplayName;
          result.supportedGroupType = 'Security';
          break;
        default:
          result.api = GSuiteApi;
          result.className = this.$style.announcement;
          result.displayName = googleDisplayName;
          result.supportedGroupType = 'Distribution';
      }
      return result;
    },

    getHeaderText() {
      const {
        displayName,
        supportedGroupType,
      } = this.directorySpecificProps;
      return `Disable Management of ${displayName} ${supportedGroupType} Groups`;
    },

    getTooltip() {
      return {
        text: this.tooltipText,
        kbLink: this.tooltipKbLink,
        kbLinkText: this.tooltipLinkText,
      };
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
    const directoryModel = StoreModelMap[this.$options.propsData.parentType];

    this.$options.computed = {
      ...this.$options.computed,
      ...mapState(directoryModel, {
        groupsEnabled: state => state.item.groupsEnabled,
        newState: state => state,
        previousItem: state => state.previousItem,
      }),
    };

    this.methods = {
      ...this.methods,
      ...mapActions(directoryModel, ['setItemField']),
    };
  },

  methods: {
    getMdTemplate(boundGroups) {
      const {
        displayName,
        supportedGroupType,
      } = this.directorySpecificProps;

      const groupsText = boundGroups && boundGroups > 0
        ? `${boundGroups} ${supportedGroupType} ${pluralize('Group', boundGroups, false)}`
        : `your ${supportedGroupType} Groups`;

      return `**Are you sure?** Disabling this will affect ${groupsText} in ${displayName}.\n
              You will lose the ability to create ${supportedGroupType} Groups in ${displayName} for any User Groups bound in JumpCloud.\n
              Do you want to continue?`;
    },

    async handleChange(checked) {
      const { api } = this.directorySpecificProps;
      const userGroupsTotalCount = await api.getUserGroupsTotalCount(this.item.id);
      const groupsEnabledChange = this.groupsEnabled && this.previousItem.groupsEnabled;

      if (!checked && groupsEnabledChange) {
        ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            mdTemplate: this.getMdTemplate(userGroupsTotalCount),
            handleCancel: () => {
              this.setItemField({ key: 'groupsEnabled', value: !checked });
              ModalEventBus.close();
            },
            handleContinue: () => {
              ModalEventBus.close();
            },
            headerText: this.getHeaderText,
            kbLink: KbArticleUrls.gettingStartedGroups,
            kbLinkText: 'Visit Getting Started: Groups to learn more.',
            mainActionAttention: true,
            showClose: true,
          },
        });
      }
      this.setItemField({ key: 'groupsEnabled', value: checked });
    },
  },

};
</script>
<style module>
@import '../../components/FormValidation/css/formFields.css';

.announcement {
  left: calc(20% + 9px);
  position: absolute;
  top: calc(50% + 9px);
}

.announcementAd {
  left: calc(21.7% + 9px);
  position: absolute;
  top: calc(77% + 9px);
}

.announcementGSuite {
  left: calc(0.8% - 69px);
  position: absolute;
  top: calc(100% + 20px);
}

.announcementCaretPosition { right: 14px; }

.distributionGroupManagement {
  position: relative;
}
</style>
